import React from "react";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import { Drawer as DrawerPrimitive } from "vaul";
import { Menu } from "lucide-react";
import PlatformSidebar, { PlatformSidebarBody } from "./PlatformSidebar";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";

/**
 * Platform shell mobile/tablet top-bar title source (M2). Longest-prefix
 * match. Fallback "Plattform" since it is genuinely the section name
 * (per Tarkan adjustment #1).
 */
const PLATFORM_ROUTE_TITLES = [
  ["/platform/organizations", "Organisationen"],
  ["/platform/audit-logs", "Audit-Protokoll"],
];

function platformRouteToTitle(pathname) {
  for (const [prefix, title] of PLATFORM_ROUTE_TITLES) {
    if (pathname === prefix || pathname.startsWith(prefix + "/")) {
      return title;
    }
  }
  return "Plattform";
}

function PlatformLayout() {
  const { user, loading, isPlatformAdminAuthenticated } = useAuth();
  const { theme } = useTheme();
  const location = useLocation();

  const [drawerOpen, setDrawerOpen] = React.useState(false);

  // Defensive close-on-route-change (M2 adjustment #2).
  React.useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const isDark = theme === "dark";
  const shellClass = isDark ? "bg-[#07090f] text-[#eef2ff]" : "bg-[#f8fafc] text-[#0f172a]";
  const mainClass = isDark ? "bg-[#07090f]" : "bg-[#f8fafc]";
  const scrollbarStyle = isDark
    ? { scrollbarColor: "rgba(255,255,255,0.12) #07090f" }
    : { scrollbarColor: "rgba(15,23,42,0.2) #f8fafc" };
  const loadingTextClass = isDark ? "text-[#6b7a9a]" : "text-[#64748b]";

  const drawerContentClass = isDark
    ? "fixed inset-y-0 left-0 z-50 flex h-full w-[280px] flex-col border-r border-white/[0.07] bg-[#0c1018] px-4 pb-4 pt-6 text-[#eef2ff] outline-none"
    : "fixed inset-y-0 left-0 z-50 flex h-full w-[280px] flex-col border-r border-black/[0.08] bg-white px-4 pb-4 pt-6 text-[#0f172a] outline-none";

  const topBarClass = isDark
    ? "lg:hidden sticky top-0 z-40 flex h-12 items-center gap-3 border-b border-white/[0.07] bg-[#07090f]/95 px-3 backdrop-blur-md"
    : "lg:hidden sticky top-0 z-40 flex h-12 items-center gap-3 border-b border-black/[0.08] bg-white/95 px-3 backdrop-blur-md";

  const hamburgerBtnClass = isDark
    ? "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] border border-white/[0.1] bg-transparent text-[#eef2ff] hover:bg-white/[0.06]"
    : "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] border border-black/[0.1] bg-transparent text-[#0f172a] hover:bg-black/[0.04]";

  const titleClass = isDark
    ? "min-w-0 flex-1 truncate text-center text-[14px] font-semibold text-[#eef2ff]"
    : "min-w-0 flex-1 truncate text-center text-[14px] font-semibold text-[#0f172a]";

  const pageTitle = platformRouteToTitle(location.pathname);

  if (loading) {
    return (
      <div
        className={`flex min-h-screen items-center justify-center ${shellClass} ${loadingTextClass}`}
      >
        <p>Lade …</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  if (!isPlatformAdminAuthenticated) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <div className={`flex min-h-screen ${shellClass}`}>
      <aside className="hidden lg:flex">
        <PlatformSidebar />
      </aside>

      <DrawerPrimitive.Root
        direction="left"
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        shouldScaleBackground={false}
      >
        <DrawerPrimitive.Portal>
          <DrawerPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80" />
          <DrawerPrimitive.Content className={drawerContentClass}>
            <DrawerPrimitive.Title className="sr-only">Navigation</DrawerPrimitive.Title>
            <PlatformSidebarBody />
          </DrawerPrimitive.Content>
        </DrawerPrimitive.Portal>
      </DrawerPrimitive.Root>

      <div
        className={`flex min-h-screen flex-1 flex-col overflow-auto ${mainClass}`}
        style={scrollbarStyle}
      >
        <header className={topBarClass}>
          <button
            type="button"
            aria-label="Menü öffnen"
            onClick={() => setDrawerOpen(true)}
            className={hamburgerBtnClass}
          >
            <Menu className="h-5 w-5" aria-hidden />
          </button>
          <span className={titleClass}>{pageTitle}</span>
          <span className="h-9 w-9 shrink-0" aria-hidden />
        </header>

        <div className="p-4 md:p-10">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

export default PlatformLayout;
